// src/pages/AddProductPage.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import { auth, db } from "../firebase";
import MenuBar from "../components/MenuBar";
import FindItemModal from "../components/FindItemModal";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Reads the locally-stored user record so we can stamp audit fields with the
 * same identifier the rest of the app shows ("admin" / "praneesh" / etc.) and
 * pair it with the Firebase Auth uid/email for forensic traceability.
 */
function readSavedUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return {};
    const u = JSON.parse(raw) || {};
    return u && typeof u === "object" ? u : {};
  } catch {
    return {};
  }
}

function buildAuditFields(action /* 'create' | 'update' */) {
  const saved = readSavedUser();
  const displayName = String(
    saved.username || saved.name || saved.email || saved.uid || saved.id || "unknown"
  ).trim() || "unknown";
  const fbUser = auth?.currentUser || null;
  const fields = {
    updatedAt: serverTimestamp(),
    updatedBy: displayName,
    updatedByUid: fbUser?.uid ?? null,
    updatedByEmail: fbUser?.email ?? saved.email ?? null,
  };
  if (action === "create") {
    fields.createdAt = serverTimestamp();
    fields.createdBy = displayName;
    fields.createdByUid = fbUser?.uid ?? null;
    fields.createdByEmail = fbUser?.email ?? saved.email ?? null;
  }
  return fields;
}

/** "$161.45" / 161.45 / "161.45" → 161.45  ·  bad input → 0 */
function parseMoneyLike(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).replace(/[^0-9.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** Render a numeric value for a `<input type="number">`: shows an empty string
 *  for 0 / null so users can start typing without backspacing the leading "0".
 *  When the user clears the input we still want the underlying state to be a
 *  Number, which the corresponding onChange handler is responsible for. */
function numericValueForInput(v) {
  if (v == null) return "";
  if (typeof v === "number") return v === 0 ? "" : String(v);
  if (typeof v === "string") {
    const s = v.trim();
    if (s === "" || s === "0") return "";
    return s;
  }
  return String(v);
}

/** Today as "DD-MM-YYYY" — matches the legacy `insdatetime` / `moddatetime` format. */
function todayDDMMYYYY() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

/**
 * Maps a Firestore `items` document — which may be in the legacy SQL/.NET
 * schema (`partno` / `alias` / `costprice` / `outwardtax` / `baffectstock` /
 * `rlevel` / `maxstock` / …) OR the newer friendly schema written by this
 * page (`code` / `desc` / `gst` / `stdCost` / …) — into the form's state shape.
 * Always prefers the friendly field if present so re-edits round-trip cleanly.
 */
function normalizeItemForForm(item) {
  if (!item || typeof item !== "object") return { ...defaultProduct };
  const code = String(
    item.code ?? item.partno ?? item.name ?? item.productid ?? ""
  ).trim();
  const desc = String(
    item.desc ?? item.description ?? item.alias ?? item.itemname ?? ""
  ).trim();
  return {
    ...defaultProduct,
    ...item,
    code,
    desc,
    group: String(item.group ?? item.gname ?? item.groupname ?? item.groupName ?? "").trim(),
    groupId: String(item.groupId ?? "").trim(),
    groupDiscount: Number(item.groupDiscount ?? 0) || 0,
    brand: String(item.brand ?? item.brandname ?? item.brandName ?? "").trim(),
    unit: String(item.unit ?? item.unitname ?? item.unitName ?? "").trim(),
    gst:
      item.gst != null && item.gst !== ""
        ? item.gst
        : item.outwardtax != null
        ? Number(item.outwardtax) || 0
        : "",
    hsn: String(item.hsn ?? "").trim(),
    affectStock: String(item.affectStock ?? item.baffectstock ?? "Y").toUpperCase() === "N" ? "N" : "Y",
    stdPurchase: item.stdPurchase != null ? Number(item.stdPurchase) || 0 : parseMoneyLike(item.purcprice),
    stdCost: item.stdCost != null ? Number(item.stdCost) || 0 : parseMoneyLike(item.costprice),
    stdSales: item.stdSales != null ? Number(item.stdSales) || 0 : parseMoneyLike(item.oprate),
    mrp: Number(item.mrp ?? 0) || 0,
    ws: Number(item.ws ?? 0) || 0,
    bulk: Number(item.bulk ?? 0) || 0,
    mech: Number(item.mech ?? 0) || 0,
    slNo: Number(item.slNo ?? item.productid ?? 0) || 0,
    reorderLevel: Number(item.reorderLevel ?? item.rlevel ?? 0) || 0,
    maxStock: Number(item.maxStock ?? item.maxstock ?? 0) || 0,
    minStock: Number(item.minStock ?? item.minstock ?? 0) || 0,
    location: String(item.location ?? "").trim(),
    empCommissionPercent:
      item.empCommissionPercent != null
        ? Number(item.empCommissionPercent) || 0
        : Number(item.commissionrate ?? 0) || 0,
    empCommissionAmount:
      item.empCommissionAmount != null
        ? Number(item.empCommissionAmount) || 0
        : parseMoneyLike(item.commissionamt),
    /** Legacy SQL FKs preserved on form state so resolver effects can fill
     *  Group / Brand / Unit text fields when only the int FK is on the item. */
    groupid: item.groupid != null && item.groupid !== "" ? item.groupid : "",
    brandid: item.brandid != null && item.brandid !== "" ? item.brandid : "",
    unitnameid: item.unitnameid != null && item.unitnameid !== "" ? item.unitnameid : "",
    /** Legacy SQL primary key for the item — used to look up MRP / Std. Cost
     *  rows in the `price` collection (PriceTypeID 4 = MRP, 2 = Std. Cost). */
    productid:
      item.productid != null && item.productid !== "" ? item.productid : "",
  };
}

/**
 * Resolves a legacy integer foreign-key (e.g., `items.hsncodeid: 472`) to the
 * matching document in the master collection. Tries — in order:
 *   1. document ID equality (`docs[i].id === "472"`) — common when SQL data is
 *      migrated to Firestore using the original primary key as the doc ID.
 *   2. any common numeric-id field on the doc (`hsncodeid`, `hsnCodeId`,
 *      `legacyId`, `id`).
 * Returns the matched document or `null`.
 */
function findMasterByLegacyId(masterList, legacyId, idFieldNames = []) {
  if (legacyId == null) return null;
  const target = String(legacyId).trim();
  if (!target || target === "0") return null;
  const list = Array.isArray(masterList) ? masterList : [];
  const byDocId = list.find((m) => String(m?.id ?? "") === target);
  if (byDocId) return byDocId;
  const fields = [
    "hsncodeid", "hsnCodeId",
    "groupid", "groupId",
    "brandid", "brandId",
    "unitnameid", "unitId", "unitid",
    "legacyId", "id",
    ...idFieldNames,
  ];
  for (const f of fields) {
    const found = list.find((m) => m?.[f] != null && String(m[f]) === target);
    if (found) return found;
  }
  return null;
}

/**
 * Builds the legacy-shape companion fields so newly written/edited items
 * stay readable by the rest of the app (BillingScreen, reports, etc.) which
 * still keys off `partno` / `alias` / `costprice` / `outwardtax` / etc.
 *
 * Note: integer foreign-key fields (`groupid` / `brandid` / `hsncodeid` /
 * `unitnameid`) are intentionally NOT written here — that would clobber the
 * legacy int64 IDs on imported docs with our Firestore string IDs. Those
 * fields are left untouched by `updateDoc` (merge semantics).
 */
function buildLegacyAliasFields(product, action /* 'create' | 'update' */) {
  const code = String(product.code ?? "").trim();
  const desc = String(product.desc ?? "").trim();
  const groupName = String(product.group ?? "").trim();
  const brandName = String(product.brand ?? "").trim();
  const unitName = String(product.unit ?? "").trim();
  const aliases = {
    partno: code,
    name: code,
    alias: desc,
    /** Mirror display names into the legacy fields on the item so reports /
     *  lists that key off `gname` / `brandname` / `unitname` keep working
     *  alongside the friendly `group` / `brand` / `unit` fields. */
    gname: groupName,
    brandname: brandName,
    unitname: unitName,
    baffectstock: product.affectStock === "N" ? "N" : "Y",
    outwardtax: Number(product.gst ?? 0) || 0,
    costprice: Number(product.stdCost ?? 0) || 0,
    purcprice: Number(product.stdPurchase ?? 0) || 0,
    oprate: Number(product.stdSales ?? 0) || 0,
    rlevel: Number(product.reorderLevel ?? 0) || 0,
    maxstock: Number(product.maxStock ?? 0) || 0,
    minstock: Number(product.minStock ?? 0) || 0,
    commissionrate: Number(product.empCommissionPercent ?? 0) || 0,
    commissionamt: Number(product.empCommissionAmount ?? 0) || 0,
    moddatetime: todayDDMMYYYY(),
  };
  if (action === "create") {
    aliases.insdatetime = todayDDMMYYYY();
  }
  return aliases;
}

const defaultProduct = {
  code: "",
  desc: "",
  group: "",
  groupId: "",
  groupDiscount: 0,
  unit: "",
  brand: "",
  gst: "",
  hsn: "",
  affectStock: "Y",
  stdPurchase: 0,
  stdCost: 0, // primary rate stored here
  stdSales: 0,
  mrp: 0,
  ws: 0,
  bulk: 0,
  mech: 0,
  slNo: 0,

  // new inventory/additional fields
  reorderLevel: 0,
  maxStock: 0,
  minStock: 0,
  location: "",
  empCommissionPercent: 0,
  empCommissionAmount: 0,

  /** Legacy int FKs matching `groups` / `brands` / `units` masters
   *  (same field names as the imported items use). */
  groupid: "",
  brandid: "",
  unitnameid: "",
  /** Legacy SQL primary key (`items.productid`) — used to fetch matching
   *  rows from the `price` collection where MRP and Std. Cost actually live
   *  for imported products (item doc itself stores them as $0.00). */
  productid: "",
};

export default function AddProductPage() {
  const [product, setProduct] = useState(defaultProduct);
  const [modalOpen, setModalOpen] = useState(false);
  /** Initial scope when opening Find (F2 / menu / Ctrl+K). Reset when modal closes. */
  const [findDefaultScope, setFindDefaultScope] = useState("all");
  const [findMenuOpen, setFindMenuOpen] = useState(false);
  const findMenuRef = useRef(null);
  const [currentId, setCurrentId] = useState(null);
  /** True while a Firestore write (save / update / delete) is in flight. Used
   *  to disable Save/Delete buttons so a double-click doesn't create duplicate
   *  documents or fire two updates back-to-back. */
  const [saving, setSaving] = useState(false);

  // masters (realtime)
  const [groups, setGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);

  // suggestions + highlight
  const [suggestions, setSuggestions] = useState({
    group: [],
    brand: [],
    unit: [],
    gst: [],
    hsn: [],
  });
  const [highlightIndex, setHighlightIndex] = useState({
    group: -1,
    brand: -1,
    unit: -1,
    gst: -1,
    hsn: -1,
  });

  // refs for inputs
  const inputRefs = useRef([]);
  useEffect(() => {
    if (!inputRefs.current || inputRefs.current.length < 40) {
      inputRefs.current = new Array(40);
    }
  }, []);

  /** Returns the index in `inputRefs.current` of the currently focused input,
   *  or -1 if no managed input has focus. Lets us restore caret position to
   *  the same field after a save instead of forcing it back to the top. */
  const findActiveInputIndex = () => {
    if (typeof document === "undefined") return -1;
    const active = document.activeElement;
    if (!active) return -1;
    const arr = inputRefs.current || [];
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === active) return i;
    }
    return -1;
  };

  /** Snapshot caret + scroll position so handleSave can restore them after the
   *  form re-renders with `defaultProduct`. */
  const captureFocusContext = () => ({
    inputIndex: findActiveInputIndex(),
    scrollY: typeof window !== "undefined" ? window.scrollY : 0,
  });

  /** Restore the caret/scroll snapshot taken by `captureFocusContext`.
   *  Falls back to the first input only if no field was focused before. */
  const restoreFocusContext = (ctx, fallbackIndex = 0) => {
    setTimeout(() => {
      const arr = inputRefs.current || [];
      const idx = ctx?.inputIndex >= 0 ? ctx.inputIndex : fallbackIndex;
      const target = arr[idx] || arr[fallbackIndex];
      try {
        target?.focus?.();
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined" && ctx?.scrollY != null) {
        window.scrollTo({ top: ctx.scrollY });
      }
    }, 60);
  };

  // debounce ref for suggestions
  const suggestTimerRef = useRef(null);
  /** Wrapper element around all autocomplete inputs; used to detect clicks
   *  outside any input/dropdown so we can close the open suggestion list. */
  const suggestWrapRef = useRef(null);

  // Auto-calc flag for MRP -> Rate
  const [autoCalcRate, setAutoCalcRate] = useState(true);

  // Tab state (only applies to left item-add area)
  const [activeTab, setActiveTab] = useState("general"); // 'general' | 'advanced'

  // helper: compute rate from mrp and group discount
  // Using user's desired behavior: apply discount directly on MRP (rate = MRP * (1 - disc%))
  const computeRateFromMRP = (mrpVal, groupDiscountPercent) => {
    const mrp = Number(mrpVal || 0);
    const discount = Number(groupDiscountPercent || 0);
    const rate = mrp * (1 - discount / 100);
    return Number(rate.toFixed(2));
  };

  // ----------------------------------------------------------------------
  // Helpers: fetch masters realtime (groups now include discount)
  // ----------------------------------------------------------------------
  useEffect(() => {
    const unsubGroups = onSnapshot(collection(db, "groups"), (snap) =>
      setGroups(
        snap.docs.map((d) => {
          const data = d.data() || {};
          // Legacy imported docs use `gname` for the display name; new docs from
          // GroupMasterPage use `name`. Read `gname` first so the imported list
          // (which is the bulk of the master) shows up correctly.
          const displayName = String(data.gname ?? data.name ?? "").trim();
          return {
            ...data,
            id: d.id,
            name: displayName,
            discount: Number(data.discount || 0),
          };
        })
      )
    );
    const unsubBrands = onSnapshot(collection(db, "brands"), (snap) =>
      setBrands(
        snap.docs.map((d) => {
          const data = d.data() || {};
          // Imported docs use `brandname`; newer master-page docs use `name`.
          return {
            ...data,
            id: d.id,
            name: String(data.brandname ?? data.name ?? "").trim(),
          };
        })
      )
    );
    const unsubUnits = onSnapshot(collection(db, "units"), (snap) =>
      setUnits(
        snap.docs.map((d) => {
          const data = d.data() || {};
          // Imported docs use `unitname`; newer master-page docs use `name`.
          return {
            ...data,
            id: d.id,
            name: String(data.unitname ?? data.name ?? "").trim(),
          };
        })
      )
    );
    const unsubGst = onSnapshot(collection(db, "gstRates"), (snap) =>
      setGstRates(snap.docs.map((d) => ({ id: d.id, rate: d.data().rate ?? d.data().name ?? "" })))
    );
    const unsubHsn = onSnapshot(collection(db, "hsnCodes"), (snap) =>
      setHsnCodes(
        snap.docs.map((d) => {
          const data = d.data() || {};
          // Imported docs store the value under `hsncode` (and have no separate
          // description); newer docs from HsnMasterPage use `code` / `name`.
          // Read all three so the dropdown shows every entry.
          const code = String(data.hsncode ?? data.code ?? data.name ?? "").trim();
          return {
            ...data,
            id: d.id,
            code,
            desc: String(data.desc ?? data.description ?? data.hsndesc ?? "").trim(),
          };
        })
      )
    );

    return () => {
      try { unsubGroups(); } catch (e) {}
      try { unsubBrands(); } catch (e) {}
      try { unsubUnits(); } catch (e) {}
      try { unsubGst(); } catch (e) {}
      try { unsubHsn(); } catch (e) {}
    };
  }, []);

  // ----------------------------------------------------------------------
  // Get next serial (used for New & after add/delete)
  // ----------------------------------------------------------------------
  const getNextSerialNo = async () => {
    try {
      const q = query(collection(db, "items"), orderBy("slNo", "desc"), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const lastRaw = snap.docs[0].data().slNo;
        const last = Number(lastRaw) || 0;
        return last + 1;
      }
      return 1;
    } catch (err) {
      console.error("getNextSerialNo", err);
      return 1;
    }
  };

  useEffect(() => {
    // initial serial
    (async () => {
      const next = await getNextSerialNo();
      setProduct((p) => ({ ...defaultProduct, slNo: next }));
    })();
  }, []);

  // ----------------------------------------------------------------------
  // CRUD: Save / Update / Delete / New
  // - All blocking `alert()` and `window.confirm()` popups are replaced by
  //   non-blocking `react-hot-toast` notifications.
  // - A `saving` flag guards every async write so a double-click on Save /
  //   Delete (or hammering F5) cannot enqueue duplicate writes.
  // ----------------------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!product.code?.trim() || !product.desc?.trim()) {
      toast.error("Part Code and Description are required");
      return;
    }

    // Snapshot where the user is right now so we can restore focus & scroll
    // after the form resets — saving should not yank them back to the top.
    const focusCtx = captureFocusContext();
    setSaving(true);
    const isUpdate = !!currentId;
    const tId = toast.loading(isUpdate ? "Updating product…" : "Saving product…");

    try {
      if (isUpdate) {
        const idSafe = String(currentId).trim();
        if (!idSafe) {
          toast.error("Lost track of the loaded item — reopen via Find.", { id: tId });
          return;
        }
        // Strip immutable / audit-managed fields from the user-edited payload
        // so they cannot be overwritten by stale local state.
        const {
          createdAt: _ca,
          createdBy: _cb,
          createdByUid: _cu,
          createdByEmail: _ce,
          updatedAt: _ua,
          updatedBy: _ub,
          updatedByUid: _uu,
          updatedByEmail: _uem,
          ...rest
        } = product;
        await updateDoc(doc(db, "items", idSafe), {
          ...rest,
          groupId: product.groupId || "",
          groupDiscount: Number(product.groupDiscount || 0),
          ...buildLegacyAliasFields(product, "update"),
          ...buildAuditFields("update"),
        });
        toast.success(`Updated "${product.code}"`, { id: tId });
        setCurrentId(null);
        const nextSlNo = await getNextSerialNo();
        setProduct({ ...defaultProduct, slNo: nextSlNo });
        restoreFocusContext(focusCtx);
        return;
      }

      // add new
      const nextSlNo = await getNextSerialNo();
      const {
        createdAt: _ca2,
        createdBy: _cb2,
        createdByUid: _cu2,
        createdByEmail: _ce2,
        updatedAt: _ua2,
        updatedBy: _ub2,
        updatedByUid: _uu2,
        updatedByEmail: _uem2,
        ...restNew
      } = product;
      const newProduct = {
        ...restNew,
        slNo: nextSlNo,
        groupId: product.groupId || "",
        groupDiscount: Number(product.groupDiscount || 0),
        ...buildLegacyAliasFields({ ...product, slNo: nextSlNo }, "create"),
        productid: Number(product.productid) || nextSlNo,
        ...buildAuditFields("create"),
      };
      await addDoc(collection(db, "items"), newProduct);
      toast.success(`Added "${product.code}"`, { id: tId });
      const next = nextSlNo + 1;
      setProduct({ ...defaultProduct, code: "", slNo: next });
      setCurrentId(null);
      restoreFocusContext(focusCtx);
    } catch (err) {
      console.error("handleSave error:", err);
      toast.error(`Save failed: ${err?.message || err}`, { id: tId });
    } finally {
      setSaving(false);
    }
    // captureFocusContext / restoreFocusContext are stable (defined inline but
    // they don't depend on anything outside useState setters and inputRefs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, currentId, saving]);

  const handleDelete = useCallback(async () => {
    if (saving) return;
    if (!currentId) {
      toast.error("Select a product to delete first.");
      return;
    }
    // Non-blocking confirm: render a toast with Delete / Cancel buttons. The
    // delete only fires if the user explicitly clicks Delete — much friendlier
    // than `window.confirm()` which freezes the whole tab.
    toast(
      (t) => (
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            Delete <strong>{product.code || "this product"}</strong>?
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                toast.dismiss(t.id);
                if (saving) return;
                setSaving(true);
                const tId = toast.loading("Deleting…");
                try {
                  await deleteDoc(doc(db, "items", currentId));
                  toast.success(`Deleted "${product.code}"`, { id: tId });
                  const nextSlNo = await getNextSerialNo();
                  setProduct({ ...defaultProduct, slNo: nextSlNo });
                  setCurrentId(null);
                  restoreFocusContext({ inputIndex: 0, scrollY: 0 });
                } catch (err) {
                  console.error("handleDelete error:", err);
                  toast.error(`Delete failed: ${err?.message || err}`, { id: tId });
                } finally {
                  setSaving(false);
                }
              }}
              className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-500"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      { duration: 8000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, product.code, saving]);

  const handleNew = useCallback(async () => {
    if (saving) return;
    const nextSlNo = await getNextSerialNo();
    setProduct({ ...defaultProduct, slNo: nextSlNo });
    setCurrentId(null);
    restoreFocusContext({ inputIndex: 0, scrollY: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving]);

  const handleUpdate = useCallback(() => {
    if (!currentId) {
      toast.error("Select an existing item to update.");
      return;
    }
    handleSave();
  }, [currentId, handleSave]);

  useEffect(() => {
    if (!findMenuOpen) return;
    const onDocClick = (e) => {
      const root = findMenuRef.current;
      if (root && !root.contains(e.target)) setFindMenuOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [findMenuOpen]);

  // ----------------------------------------------------------------------
  // Keyboard shortcuts: F2 Find, F3 New, F4 Modify, F5 Save
  // - We pin the latest handlers in refs so the keydown listener does NOT
  //   re-attach on every keystroke (the previous version did, because the
  //   effect depended on `product` and `currentId`).
  // ----------------------------------------------------------------------
  const handlersRef = useRef({ handleSave, handleNew });
  useEffect(() => {
    handlersRef.current = { handleSave, handleNew };
  }, [handleSave, handleNew]);
  const currentIdRef = useRef(currentId);
  useEffect(() => {
    currentIdRef.current = currentId;
  }, [currentId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setFindDefaultScope("all");
        setModalOpen(true);
      } else if ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setFindDefaultScope("all");
        setModalOpen(true);
      } else if (e.key === "F3") {
        e.preventDefault();
        handlersRef.current.handleNew();
      } else if (e.key === "F4") {
        e.preventDefault();
        if (currentIdRef.current) handlersRef.current.handleSave();
      } else if (e.key === "F5") {
        e.preventDefault();
        handlersRef.current.handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ----------------------------------------------------------------------
  // Enter navigation for inputs
  // ----------------------------------------------------------------------
  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = inputRefs.current[index + 1];
      if (next && typeof next.focus === "function") next.focus();
      else handleSave();
    }
  };

  // ----------------------------------------------------------------------
  // Suggestions logic (filtering local masters) with debounce
  // ----------------------------------------------------------------------
  const handleSuggestChange = (field, value, source) => {
    // clear groupId if user types new text
    if (field === "group") {
      setProduct((p) => ({
        ...p,
        [field]: value,
        groupId: "",
        groupDiscount: p.groupDiscount || 0,
        groupid: "",
      }));
    } else if (field === "hsn") {
      // The user is editing HSN by hand → drop the legacy int FK so the
      // resolver doesn't snap it back to the old value on the next render.
      setProduct((p) => ({ ...p, hsn: value, hsncodeid: "" }));
    } else if (field === "brand") {
      setProduct((p) => ({ ...p, brand: value, brandid: "" }));
    } else if (field === "unit") {
      setProduct((p) => ({ ...p, unit: value, unitnameid: "" }));
    } else {
      setProduct((p) => ({ ...p, [field]: value }));
    }

    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);

    if (!value) {
      // clear immediately when empty
      setSuggestions((s) => ({ ...s, [source]: [] }));
      setHighlightIndex((h) => ({ ...h, [source]: -1 }));
      return;
    }

    suggestTimerRef.current = setTimeout(() => {
      let filtered = [];
      const v = value.toString().toLowerCase();
      if (source === "group") filtered = groups.filter((g) => (g.name || "").toLowerCase().includes(v));
      if (source === "brand") filtered = brands.filter((b) => (b.name || "").toLowerCase().includes(v));
      if (source === "unit") filtered = units.filter((u) => (u.name || "").toLowerCase().includes(v));
      if (source === "gst") filtered = gstRates.filter((g) => String(g.rate).toLowerCase().includes(v));
      if (source === "hsn")
        filtered = hsnCodes.filter((h) => (String(h.code || "").toLowerCase().includes(v) || (h.desc || "").toLowerCase().includes(v)));

      setSuggestions((s) => ({ ...s, [source]: filtered.slice(0, 50) }));
      setHighlightIndex((h) => ({ ...h, [source]: filtered.length ? 0 : -1 }));
    }, 180); // 180ms debounce
  };

  useEffect(() => {
    return () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    };
  }, []);

  /** Close any open suggestion dropdown when the user clicks outside the
   *  autocomplete area. Without this the dropdown only closes on Escape /
   *  selection / blur — which feels broken if the user clicks elsewhere
   *  on the page expecting it to dismiss. */
  useEffect(() => {
    const onDocMouseDown = (e) => {
      const wrap = suggestWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target)) return;
      setSuggestions({ group: [], brand: [], unit: [], gst: [], hsn: [] });
      setHighlightIndex({ group: -1, brand: -1, unit: -1, gst: -1, hsn: -1 });
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // commitSuggestion enhanced: for group include id and discount and auto-calc rate
  const commitSuggestion = (field, source, item) => {
    if (!item) return;
    if (source === "group") {
      // item: { id, name, discount } — may also carry legacy `groupid` on imported masters
      const groupName = item.name;
      const groupId = item.id;
      const groupDiscount = Number(item.discount || 0);
      const legacyGroupFk =
        item.groupid ??
        (typeof item.groupId === "number" ? item.groupId : null) ??
        (/^\d+$/.test(String(item.id ?? "")) ? Number(item.id) : null);
      setProduct((p) => {
        const newState = {
          ...p,
          group: groupName,
          groupId,
          groupDiscount,
          groupid: legacyGroupFk != null ? legacyGroupFk : "",
        };
        // if auto-calc enabled, compute stdCost using both MRP and groupDiscount
        if (autoCalcRate) {
          newState.stdCost = computeRateFromMRP(newState.mrp, groupDiscount);
        }
        // recalc emp commission amount if percent set
        if (Number(newState.empCommissionPercent || 0) !== 0) {
          newState.empCommissionAmount = Number(((newState.stdCost || 0) * Number(newState.empCommissionPercent || 0) / 100).toFixed(2));
        }
        return newState;
      });
    } else if (source === "hsn") {
      // Picked an HSN from the dropdown — set the visible code AND, if the
      // master doc carries a legacy numeric id, sync `hsncodeid` so legacy
      // readers (e.g., old reports keyed on hsncodeid) match the new value.
      const value = item.code ?? "";
      const legacyId =
        item.hsncodeid ??
        item.hsnCodeId ??
        item.legacyId ??
        (/^\d+$/.test(String(item.id || "")) ? Number(item.id) : null);
      setProduct((p) => ({
        ...p,
        hsn: value,
        hsncodeid: legacyId != null ? legacyId : "",
      }));
    } else {
      let value = "";
      if (source === "gst") value = String(item.rate);
      else value = item.name;
      setProduct((p) => ({ ...p, [field]: source === "gst" ? Number(value) : value }));
    }
    setSuggestions((s) => ({ ...s, [source]: [] }));
    setHighlightIndex((h) => ({ ...h, [source]: -1 }));
  };

  const handleSuggestKeyDown = (e, field, source, idx) => {
    const list = suggestions[source] || [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!list.length) return;
      setHighlightIndex((h) => ({ ...h, [source]: Math.min((h[source] + 1) || 0, list.length - 1) }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!list.length) return;
      setHighlightIndex((h) => ({ ...h, [source]: Math.max((h[source] - 1) || 0, 0) }));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selIdx = highlightIndex[source] >= 0 ? highlightIndex[source] : 0;
      const sel = list[selIdx];
      if (sel) {
        commitSuggestion(field, source, sel);
        setTimeout(() => inputRefs.current[idx + 1]?.focus?.(), 10);
      } else {
        handleKeyDown(e, idx);
      }
    } else if (e.key === "Escape") {
      // close suggestions
      setSuggestions((s) => ({ ...s, [source]: [] }));
      setHighlightIndex((h) => ({ ...h, [source]: -1 }));
    } else {
      handleKeyDown(e, idx);
    }
  };

  const selectSuggestion = (field, value, source, item) => {
    commitSuggestion(field, source, item ?? { name: value, rate: value, code: value });
    const idxMap = { group: 2, brand: 3, unit: 4, gst: 5, hsn: 6 };
    setTimeout(() => inputRefs.current[idxMap[source] + 1]?.focus?.(), 10);
  };

  // ----------------------------------------------------------------------
  // When FindItemModal returns an item
  // ----------------------------------------------------------------------
  const handleFindSelect = (item) => {
    if (!item) return;
    setProduct(normalizeItemForForm(item));
    setCurrentId(item.id || null);
    setModalOpen(false);
    setTimeout(() => inputRefs.current[1]?.focus?.(), 80);
  };

  // ----------------------------------------------------------------------
  // Helper: when STD.COST (stdCost) changes, recalc commission amount if percent present
  // ----------------------------------------------------------------------
  const onStdCostChange = (val) => {
    const num = Number(val || 0);
    setProduct((p) => {
      const next = { ...p, stdCost: num };
      if (Number(next.empCommissionPercent || 0) !== 0) {
        next.empCommissionAmount = Number(((next.stdCost || 0) * Number(next.empCommissionPercent || 0) / 100).toFixed(2));
      }
      return next;
    });
  };

  // commission percent change handler
  const onEmpCommissionPercentChange = (val) => {
    const pct = Number(val || 0);
    setProduct((p) => {
      const next = { ...p, empCommissionPercent: pct };
      next.empCommissionAmount = Number(((next.stdCost || 0) * pct / 100).toFixed(2));
      return next;
    });
  };

  // manual override commission amount (if user edits amount directly)
  const onEmpCommissionAmountChange = (val) => {
    const amt = Number(val || 0);
    setProduct((p) => ({ ...p, empCommissionAmount: amt }));
  };

  // ----------------------------------------------------------------------
  // RESOLVE LEGACY FK → MASTER NAMES
  // - Legacy items store integer foreign keys: hsncodeid (e.g. 472).
  // - When such an item is loaded into the form, the friendly `hsn` field is
  //   blank. Look up the matching `hsnCodes` document and fill the field so
  //   the user sees the actual HSN value.
  // - Runs whenever the loaded item or the masters change. Won't overwrite a
  //   value the user is currently typing (only fills when `hsn` is empty).
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!product?.hsncodeid) return;
    if (String(product.hsn || "").trim() !== "") return;
    if (!hsnCodes || hsnCodes.length === 0) return;
    const match = findMasterByLegacyId(hsnCodes, product.hsncodeid);
    if (!match) return;
    const value = String(match.code ?? match.name ?? "").trim();
    if (!value) return;
    setProduct((p) => (String(p.hsn || "").trim() ? p : { ...p, hsn: value }));
  }, [product.hsncodeid, product.hsn, hsnCodes]);

  // Legacy `items.brandid` (int) ↔ `brands` collection (doc id or `brandid` field)
  useEffect(() => {
    const legacy = product.brandid;
    if (legacy == null || legacy === "") return;
    if (Number(legacy) === 0) return;
    if (!brands?.length) return;
    if (String(product.brand || "").trim() !== "") return;
    const match = findMasterByLegacyId(brands, legacy);
    if (!match) return;
    const value = String(match.name ?? "").trim();
    if (!value) return;
    setProduct((p) =>
      String(p.brand || "").trim() ? p : { ...p, brand: value, brandid: legacy }
    );
  }, [product.brandid, product.brand, brands]);

  // Legacy `items.unitnameid` (int) ↔ `units` collection (doc id or `unitnameid`/`unitid` field)
  useEffect(() => {
    const legacy = product.unitnameid;
    if (legacy == null || legacy === "") return;
    if (Number(legacy) === 0) return;
    if (!units?.length) return;
    if (String(product.unit || "").trim() !== "") return;
    const match = findMasterByLegacyId(units, legacy);
    if (!match) return;
    const value = String(match.name ?? "").trim();
    if (!value) return;
    setProduct((p) =>
      String(p.unit || "").trim() ? p : { ...p, unit: value, unitnameid: legacy }
    );
  }, [product.unitnameid, product.unit, units]);

  // ----------------------------------------------------------------------
  // Legacy `price` collection lookup
  // - Imported items store $0.00 in `costprice` / `oprate` and put real
  //   amounts in a separate `price` collection keyed by `productid`.
  //     PriceTypeID 4 → MRP
  //     PriceTypeID 2 → Std. Cost (rate)
  // - On load, when `product.productid` is present and MRP/Std. Cost are
  //   still empty, query `price` and fill in whatever rows exist.
  // - Won't overwrite values the user has typed.
  // ----------------------------------------------------------------------
  useEffect(() => {
    const pid = product?.productid;
    if (pid == null || pid === "") return;
    const pidNum = Number(pid);
    if (!Number.isFinite(pidNum) || pidNum === 0) return;

    const needMrp = !(Number(product.mrp) > 0);
    const needCost = !(Number(product.stdCost) > 0);
    if (!needMrp && !needCost) return;

    let cancelled = false;
    (async () => {
      try {
        // Try the integer form first (matches `productid: 1212653567` int64).
        let snap = await getDocs(
          query(collection(db, "price"), where("productid", "==", pidNum))
        );
        // Some imports store productid as a string — fall back to that if the
        // integer query returns nothing.
        if (snap.empty) {
          snap = await getDocs(
            query(collection(db, "price"), where("productid", "==", String(pidNum)))
          );
        }
        if (cancelled || snap.empty) return;

        let mrp;
        let cost;
        for (const d of snap.docs) {
          const data = d.data() || {};
          const t = Number(data.PriceTypeID ?? data.pricetypeid ?? data.priceTypeId);
          const amt =
            data.Price != null
              ? parseMoneyLike(data.Price)
              : parseMoneyLike(data.price);
          if (!Number.isFinite(amt) || amt <= 0) continue;
          if (t === 4 && mrp == null) mrp = amt;
          else if (t === 2 && cost == null) cost = amt;
        }
        if (mrp == null && cost == null) return;

        setProduct((p) => {
          const next = { ...p };
          if (needMrp && mrp != null && !(Number(p.mrp) > 0)) next.mrp = mrp;
          if (needCost && cost != null && !(Number(p.stdCost) > 0)) {
            next.stdCost = cost;
          }
          // Only auto-derive Std. Cost from MRP when:
          //  - the price collection did NOT give us an explicit cost, AND
          //  - auto-calc is on, AND
          //  - the group has a real discount (>0) to apply.
          // Otherwise the formula collapses to MRP*(1-0%)=MRP and would clobber
          // the imported cost with the MRP value.
          const discount = Number(next.groupDiscount || 0);
          if (
            autoCalcRate &&
            needMrp &&
            mrp != null &&
            cost == null &&
            discount > 0 &&
            !(Number(p.stdCost) > 0)
          ) {
            next.stdCost = computeRateFromMRP(mrp, discount);
          }
          if (Number(next.empCommissionPercent || 0) !== 0) {
            next.empCommissionAmount = Number(
              ((next.stdCost || 0) * Number(next.empCommissionPercent || 0) / 100).toFixed(2)
            );
          }
          return next;
        });
      } catch (err) {
        console.error("[AddProductPage] price lookup failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally re-run only when the legacy product id changes; we don't
    // want this firing every time the user edits MRP / Std. Cost.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.productid]);

  // Legacy `items.groupid` (int) ↔ `groups` collection (doc id or `groupid` field)
  useEffect(() => {
    const legacy = product.groupid;
    if (legacy == null || legacy === "") return;
    if (Number(legacy) === 0) return;
    if (!groups?.length) return;
    const match = findMasterByLegacyId(groups, legacy);
    if (!match) return;
    const name = String(match.name ?? "").trim();
    const fsId = String(match.id ?? "").trim();
    const discount = Number(match.discount ?? 0) || 0;
    if (!name && !fsId) return;
    setProduct((p) => {
      const hasName = String(p.group || "").trim() !== "";
      const hasFsId = String(p.groupId || "").trim() !== "";
      if (hasName && hasFsId) return p;
      const next = {
        ...p,
        group: name || p.group,
        groupId: fsId || p.groupId,
        groupDiscount: discount,
        groupid: legacy,
      };
      // Only auto-derive Std. Cost from MRP when the group has a real discount
      // and there isn't already an explicit cost on the form. Otherwise the
      // formula collapses to MRP (discount = 0%) and clobbers an imported cost.
      if (
        autoCalcRate &&
        next.mrp != null &&
        Number(next.groupDiscount || 0) > 0 &&
        !(Number(p.stdCost) > 0)
      ) {
        next.stdCost = computeRateFromMRP(next.mrp, next.groupDiscount);
      }
      if (Number(next.empCommissionPercent || 0) !== 0) {
        next.empCommissionAmount = Number(
          ((next.stdCost || 0) * Number(next.empCommissionPercent || 0) / 100).toFixed(2)
        );
      }
      return next;
    });
  }, [product.groupid, groups, autoCalcRate, product.group, product.groupId]);

  // ----------------------------------------------------------------------
  // AUTO-APPLY GROUP WHEN TYPED OR SELECTED
  // - If user types an exact group name, apply its id & discount automatically
  // - If startsWith match found, partially apply discount (do not override visible typed text)
  // - When applied, and autoCalcRate is true, recompute stdCost from MRP & groupDiscount
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!product.group || !groups || groups.length === 0) return;
    const typed = (product.group || "").toString().trim().toLowerCase();
    if (!typed) return;

    // exact match preference
    const exact = groups.find(g => (g.name || "").toLowerCase() === typed);
    if (exact) {
      if (product.groupId !== exact.id || Number(product.groupDiscount || 0) !== Number(exact.discount || 0)) {
        setProduct(p => {
          const next = {
            ...p,
            group: exact.name,
            groupId: exact.id,
            groupDiscount: Number(exact.discount || 0),
          };
          // Only auto-derive Std. Cost from MRP when the group has a real
          // discount. With discount=0 the formula collapses to MRP and would
          // wipe out an imported / user-typed cost.
          if (
            autoCalcRate &&
            Number(next.groupDiscount || 0) > 0 &&
            !(Number(p.stdCost) > 0)
          ) {
            next.stdCost = computeRateFromMRP(next.mrp, next.groupDiscount);
          }
          if (Number(next.empCommissionPercent || 0) !== 0) {
            next.empCommissionAmount = Number(((next.stdCost || 0) * Number(next.empCommissionPercent || 0) / 100).toFixed(2));
          }
          return next;
        });
      }
      return;
    }

    // fallback: startsWith single candidate (do not override the typed visible group name)
    const starts = groups.find(g => (g.name || "").toLowerCase().startsWith(typed));
    if (starts) {
      if (product.groupId !== starts.id) {
        setProduct(p => {
          const next = {
            ...p,
            groupId: starts.id, // attach id
            groupDiscount: Number(starts.discount || 0),
          };
          if (
            autoCalcRate &&
            Number(next.groupDiscount || 0) > 0 &&
            !(Number(p.stdCost) > 0)
          ) {
            next.stdCost = computeRateFromMRP(next.mrp, next.groupDiscount);
          }
          if (Number(next.empCommissionPercent || 0) !== 0) {
            next.empCommissionAmount = Number(((next.stdCost || 0) * Number(next.empCommissionPercent || 0) / 100).toFixed(2));
          }
          return next;
        });
      }
    }
  }, [product.group, groups, autoCalcRate]);
  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------
  // ----------------------------------------------------------------------
  // Render — light theme, two-column main area: left = form, right = quick
  // actions + preview (stacks on small screens). Title + Code row spans full
  // width above the columns.
  // ----------------------------------------------------------------------
  // Reusable Tailwind class strings keep the JSX scannable and ensure every
  // input on the page is themed identically.
  const inputCls =
    "w-full bg-white border border-slate-300 px-2 py-1 rounded text-slate-800 text-xs " +
    "focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";
  const inputNumCls = inputCls + " text-right";
  const labelCls = "block text-xs font-medium text-slate-600 mb-1";
  const sectionH3 = "text-sm font-semibold text-slate-700";
  const dropdownCls =
    "absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto z-20 text-xs";

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50 text-xs text-slate-800">
      <div className="w-full shrink-0 shadow z-50">
        <MenuBar />
      </div>

      {/* Compact title strip — keeps everything visible without page scroll. */}
      <div className="-mt-2 shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 py-1.5 bg-white border-b border-slate-200">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold text-slate-800">Product Page</h1>
          <span className="text-[11px] text-slate-500 hidden sm:inline">
            F2 / Ctrl+K Find · F3 New · F4 Update · F5 Save
          </span>
        </div>
        <div className="flex items-center gap-2">
          {currentId ? (
            <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              editing
            </span>
          ) : null}
          <span className="text-[11px] text-slate-400">SL</span>
          <span className="text-base font-semibold text-indigo-600 tabular-nums min-w-[2rem] text-right">
            {product.slNo || "-"}
          </span>
        </div>
      </div>

      {/* Single fit-to-screen frame: left = form, right = quick actions + preview.
          No outer scroll — each column manages its own overflow if content is taller
          than the available height. */}
      <main className="flex-1 min-h-0 flex flex-col lg:flex-row gap-2 px-2 py-2 overflow-hidden">
        <section className="flex-1 lg:basis-1/2 min-h-0 min-w-0 flex flex-col overflow-hidden">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-2.5 flex-1 min-h-0 overflow-y-auto">
            <>
                <div className="flex gap-1.5 mb-2 border-b border-slate-200 pb-1.5">
                  <button
                    type="button"
                    onClick={() => setActiveTab("general")}
                    className={
                      "px-3 py-1 rounded-md text-xs transition-colors " +
                      (activeTab === "general"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200")
                    }
                  >
                    General
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("advanced")}
                    className={
                      "px-3 py-1 rounded-md text-xs transition-colors " +
                      (activeTab === "advanced"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200")
                    }
                  >
                    Advanced
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end mb-4">
                  <div>
                    <label className={labelCls}>Code</label>
                    <input
                      ref={(el) => (inputRefs.current[0] = el)}
                      value={product.code}
                      onChange={(e) => setProduct((p) => ({ ...p, code: e.target.value }))}
                      onKeyDown={(e) => handleKeyDown(e, 0)}
                      className={inputCls}
                      disabled={!!currentId}
                    />
                  </div>
                  <div />
                </div>
                {activeTab === "general" ? (
                  <>
                {/* Description */}
                <div className="mb-0.5">
                  <label className={labelCls}>Description</label>
                  <input
                    ref={(el) => (inputRefs.current[1] = el)}
                    value={product.desc}
                    onChange={(e) => setProduct((p) => ({ ...p, desc: e.target.value }))}
                    onKeyDown={(e) => handleKeyDown(e, 1)}
                    className={inputCls}
                  />
                </div>

                {/* Group + Brand + Unit + GST + HSN — wrapped so click-away
                    closes any open suggestion dropdown. */}
                <div ref={suggestWrapRef}>
                  <div className="grid grid-cols-2 gap-3 mb-1">
                    {[
                      { key: "group", label: "Group", source: "group", idx: 2 },
                      { key: "brand", label: "Brand", source: "brand", idx: 3 },
                    ].map(({ key, label, source, idx }) => (
                      <div key={key} className="relative">
                        <label className={labelCls}>{label}</label>
                        <input
                          ref={(el) => (inputRefs.current[idx] = el)}
                          value={product[key]}
                          onChange={(e) => handleSuggestChange(key, e.target.value, source)}
                          onKeyDown={(e) => handleSuggestKeyDown(e, key, source, idx)}
                          className={inputCls}
                        />
                        {suggestions[source]?.length > 0 && (
                          <ul className={dropdownCls}>
                            {suggestions[source].map((s, i) => (
                              <li
                                key={s.id ?? `${source}-${i}`}
                                onClick={() => selectSuggestion(key, s.name, source, s)}
                                className={
                                  "px-3 py-1.5 cursor-pointer " +
                                  (highlightIndex[source] === i
                                    ? "bg-indigo-50 text-indigo-700"
                                    : "hover:bg-slate-50 text-slate-700")
                                }
                              >
                                {source === "group"
                                  ? `${s.name} — ${Number(s.discount || 0).toFixed(2)}%`
                                  : s.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-1">
                    {[
                      { key: "unit", label: "Unit", source: "unit", idx: 4 },
                      { key: "gst", label: "GST %", source: "gst", idx: 5 },
                      { key: "hsn", label: "HSN Code", source: "hsn", idx: 6 },
                    ].map(({ key, label, source, idx }) => (
                      <div key={key} className="relative">
                        <label className={labelCls}>{label}</label>
                        <input
                          ref={(el) => (inputRefs.current[idx] = el)}
                          value={product[key]}
                          onChange={(e) => handleSuggestChange(key, e.target.value, source)}
                          onKeyDown={(e) => handleSuggestKeyDown(e, key, source, idx)}
                          className={inputCls}
                        />
                        {suggestions[source]?.length > 0 && (
                          <ul className={dropdownCls}>
                            {suggestions[source].map((s, i) => {
                              const labelTxt =
                                source === "gst"
                                  ? `${s.rate}%`
                                  : source === "hsn"
                                  ? `${s.code} — ${s.desc}`
                                  : s.name;
                              return (
                                <li
                                  key={s.id ?? `${source}-${i}`}
                                  onClick={() =>
                                    selectSuggestion(
                                      key,
                                      source === "hsn"
                                        ? s.code
                                        : source === "gst"
                                        ? String(s.rate)
                                        : s.name,
                                      source,
                                      s
                                    )
                                  }
                                  className={
                                    "px-3 py-1.5 cursor-pointer " +
                                    (highlightIndex[source] === i
                                      ? "bg-indigo-50 text-indigo-700"
                                      : "hover:bg-slate-50 text-slate-700")
                                  }
                                >
                                  {labelTxt}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Std Pricing */}
                <div className="mt-3 pt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={sectionH3}>Std. Pricing</h3>
                    <label className="text-xs flex items-center gap-2 text-slate-600">
                      <input
                        type="checkbox"
                        className="accent-indigo-600"
                        checked={autoCalcRate}
                        onChange={(e) => setAutoCalcRate(e.target.checked)}
                      />
                      <span>Auto-calc Rate from MRP (apply group discount)</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "stdPurchase", label: "STD. PURCHASE" },
                      { key: "stdCost", label: "STD. COST (Rate)" },
                      { key: "stdSales", label: "STD. SALES" },
                      { key: "mrp", label: "MRP" },
                      { key: "ws", label: "WS" },
                      { key: "bulk", label: "BULK" },
                      { key: "mech", label: "MECH" },
                    ].map((row, idx) => {
                      const refIndex = 7 + idx;
                      if (row.key === "mrp") {
                        return (
                          <div key={row.key} className="flex items-start gap-2">
                            <div className="w-36 text-xs font-medium text-slate-600 pt-2">
                              {row.label}
                            </div>
                            <div>
                              <input
                                ref={(el) => (inputRefs.current[refIndex] = el)}
                                type="number"
                                value={numericValueForInput(product.mrp)}
                                onChange={(e) => {
                                  const mrpVal = Number(e.target.value || 0);
                                  setProduct((p) => {
                                    const next = { ...p, mrp: mrpVal };
                                    if (autoCalcRate) {
                                      next.stdCost = computeRateFromMRP(
                                        mrpVal,
                                        p.groupDiscount || 0
                                      );
                                    }
                                    if (Number(next.empCommissionPercent || 0) !== 0) {
                                      next.empCommissionAmount = Number(
                                        (
                                          ((next.stdCost || 0) *
                                            Number(next.empCommissionPercent || 0)) /
                                          100
                                        ).toFixed(2)
                                      );
                                    }
                                    return next;
                                  });
                                }}
                                onKeyDown={(e) => handleKeyDown(e, refIndex)}
                                className={inputNumCls + " w-36"}
                              />
                              <div className="text-[11px] text-slate-500 mt-1">
                                Computed Rate (stored in STD. COST):{" "}
                                {computeRateFromMRP(
                                  product.mrp,
                                  product.groupDiscount || 0
                                ).toFixed(2)}
                                {product.group
                                  ? ` (group disc: ${Number(
                                      product.groupDiscount || 0
                                    ).toFixed(2)}%)`
                                  : ""}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={row.key} className="flex items-center gap-2">
                          <div className="w-36 text-xs font-medium text-slate-600">
                            {row.label}
                          </div>
                          <input
                            ref={(el) => (inputRefs.current[refIndex] = el)}
                            type="number"
                            value={numericValueForInput(
                              row.key === "stdCost" ? product.stdCost : product[row.key]
                            )}
                            onChange={(e) =>
                              row.key === "stdCost"
                                ? onStdCostChange(e.target.value)
                                : setProduct((p) => ({
                                    ...p,
                                    [row.key]: Number(e.target.value || 0),
                                  }))
                            }
                            onKeyDown={(e) => handleKeyDown(e, refIndex)}
                            className={inputNumCls + " w-36"}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                </>
                ) : (
              <div>
                <h3 className={sectionH3 + " mb-4"}>Inventory / Additional Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Reorder Level</label>
                    <input
                      ref={(el) => (inputRefs.current[30] = el)}
                      type="number"
                      value={numericValueForInput(product.reorderLevel)}
                      onChange={(e) =>
                        setProduct((p) => ({
                          ...p,
                          reorderLevel: Number(e.target.value || 0),
                        }))
                      }
                      className={inputNumCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Max Stock</label>
                    <input
                      ref={(el) => (inputRefs.current[31] = el)}
                      type="number"
                      value={numericValueForInput(product.maxStock)}
                      onChange={(e) =>
                        setProduct((p) => ({
                          ...p,
                          maxStock: Number(e.target.value || 0),
                        }))
                      }
                      className={inputNumCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Min Stock</label>
                    <input
                      ref={(el) => (inputRefs.current[32] = el)}
                      type="number"
                      value={numericValueForInput(product.minStock)}
                      onChange={(e) =>
                        setProduct((p) => ({
                          ...p,
                          minStock: Number(e.target.value || 0),
                        }))
                      }
                      className={inputNumCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Location</label>
                    <input
                      ref={(el) => (inputRefs.current[33] = el)}
                      type="text"
                      value={product.location}
                      onChange={(e) =>
                        setProduct((p) => ({ ...p, location: e.target.value }))
                      }
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200">
                  <h4 className={sectionH3 + " mb-3"}>Employee Commission</h4>
                  <div className="grid grid-cols-3 gap-4 items-end">
                    <div>
                      <label className={labelCls}>Commission %</label>
                      <input
                        ref={(el) => (inputRefs.current[34] = el)}
                        type="number"
                        step="0.01"
                        value={numericValueForInput(product.empCommissionPercent)}
                        onChange={(e) =>
                          onEmpCommissionPercentChange(e.target.value)
                        }
                        className={inputNumCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Commission Amount</label>
                      <input
                        ref={(el) => (inputRefs.current[35] = el)}
                        type="number"
                        step="0.01"
                        value={numericValueForInput(product.empCommissionAmount)}
                        onChange={(e) =>
                          onEmpCommissionAmountChange(e.target.value)
                        }
                        className={inputNumCls}
                      />
                      <div className="text-[11px] text-slate-500 mt-1">
                        Calculated as STD.COST × % (auto-updates)
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Base for Commission</label>
                      <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 px-2 py-2 rounded">
                        {`STD. COST: ${Number(product.stdCost || 0).toFixed(2)}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                )}
            </>
          </div>
        </section>

        <aside className="flex-1 lg:basis-1/2 flex flex-col gap-2 w-full min-h-0 overflow-hidden">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-2.5 shrink-0">
            <h3 className={sectionH3 + " mb-2 text-xs uppercase tracking-wide"}>Quick Actions</h3>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                "Stock Alias Composition",
                "Custom Group",
                "Stock Group (F8)",
                "Storage Space (F7)",
                "Br./Dept. Inventory (F6)",
                "Alternative Unit(s) (F9)",
                "Product Browser",
                "Refresh Popups",
                "Custom Fields",
              ].map((btn) => (
                <button
                  key={btn}
                  type="button"
                  className="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded text-left text-[10px] text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
                >
                  {btn}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-2.5 flex-1 min-h-0 overflow-y-auto">
            <h3 className={sectionH3 + " mb-2 text-xs uppercase tracking-wide"}>Preview</h3>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
              {[
                ["Code", product.code || "-"],
                ["Desc", product.desc || "-"],
                ["Group", product.group || "-"],
                ["Group Disc", `${Number(product.groupDiscount || 0).toFixed(2)}%`],
                ["STD. COST", Number(product.stdCost || 0).toFixed(2)],
                ["Reorder Lvl", product.reorderLevel],
                ["Max Stock", product.maxStock],
                ["Min Stock", product.minStock],
                ["Location", product.location || "-"],
                [
                  "Emp Comm.",
                  `${Number(product.empCommissionPercent || 0).toFixed(2)}% → ${Number(
                    product.empCommissionAmount || 0
                  ).toFixed(2)}`,
                ],
                ["Brand", product.brand || "-"],
                ["GST", product.gst || "-"],
                ["SL No", product.slNo || "-"],
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-slate-800 font-medium truncate" title={String(v)}>{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </aside>
      </main>

      {/* Footer actions — disabled while a write is in flight so a quick
          double-click on Save / Delete / Modify cannot enqueue duplicate
          Firestore writes. */}
      <div className="flex shrink-0 justify-end gap-1.5 px-3 py-1.5 bg-white border-t border-slate-200 shadow-[0_-1px_2px_rgba(0,0,0,0.04)]">
        <button
          onClick={handleNew}
          disabled={saving}
          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          New (F3)
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save (F5)"}
        </button>
        <button
          onClick={handleUpdate}
          disabled={!currentId || saving}
          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-amber-500 text-white shadow-sm hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Update (F4)
        </button>
        <div className="relative inline-flex" ref={findMenuRef}>
          <span className="inline-flex rounded-md shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => {
                setFindDefaultScope("all");
                setFindMenuOpen(false);
                setModalOpen(true);
              }}
              className="inline-flex items-center px-2.5 py-1 rounded-l-md text-[11px] bg-sky-600 text-white shadow-sm hover:bg-sky-700"
            >
              Find (F2)
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setFindMenuOpen((o) => !o);
              }}
              className="inline-flex items-center px-1.5 py-1 rounded-r-md text-[11px] bg-sky-700 text-white border-l border-sky-500 hover:bg-sky-800"
              aria-expanded={findMenuOpen}
              aria-haspopup="menu"
              title="Open find narrowed to one field"
            >
              ▾
            </button>
          </span>
          {findMenuOpen ? (
            <ul
              className="absolute bottom-full right-0 mb-1 min-w-[11rem] rounded-md border border-slate-200 bg-white py-1 text-left shadow-lg z-[60]"
              role="menu"
            >
              {[
                ["code", "Code only"],
                ["desc", "Description"],
                ["group", "Group"],
                ["brand", "Brand"],
                ["unit", "Unit"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50"
                    onClick={() => {
                      setFindDefaultScope(id);
                      setFindMenuOpen(false);
                      setModalOpen(true);
                    }}
                  >
                    Find: {label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <button
          onClick={handleDelete}
          disabled={!currentId || saving}
          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-rose-600 text-white shadow-sm hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Delete
        </button>
      </div>

      {/* Find modal */}
      <FindItemModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setFindDefaultScope("all");
          setFindMenuOpen(false);
        }}
        onSelect={handleFindSelect}
        defaultSearchScope={findDefaultScope}
      />
      {/* Toast host — every save / delete / validation message renders here. */}
      <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
    </div>
  );
}
